import React, { ComponentProps, useEffect, useState } from "react";
import { ErrorView } from "../../components/error-view/ErrorView";
import { LoadingView } from "../../components/loading-view/LoadingView";
import { ContentModal } from "../../components/content-modal/ContentModal";
import { usePageQuery } from "../../shared/hooks/usePageQuery";
import { prefetchListQuery } from "../../../utils/pageSizeMemory";
import {
  PagedComponentState,
  usePagedComponentStateMachine,
} from "../../shared/hooks/usePageComponentStateMachine";
import { useRepositoriesContext } from "../../../contexts/RepositoriesContext";
import { useCustomerAndVendors } from "../../shared/hooks/useCustomerAndVendors";
import { Design } from "../../../core/models/entities/Design";
import { DesignListWrapper } from "./wrappers/design-list.wrapper";
import { DesignDetailsWrapper } from "./wrappers/design-details.wrapper";
import { getAllQuery } from "../logo-editor/logo-editor.screen";
import { RejectionNoteDialog } from "../../components/Dialogs/rejection-note-popup.component";
import { DesignTitleHeader } from "../../components/Logo/LogoTitleHeader";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { useDialog } from "../../components/Dialogs/dialog-base";
import { useOpenDesign } from "../logo-list/vmHooks/useOpenDesign";
import { DesignModalIntent } from "./components/design-details.component";
import { useLocation } from "react-router-dom";
import { designListGate } from "../../shared/functions/listGates";
import { enviroment } from "../../../enviroment";
import { shouldPrefetchDesignPage } from "../../../utils/functions/designPrefetch";
import { useDesignActionHandler } from "./hooks/useDesignActionHandler";
import { canEditDesignFromRejectionNote } from "./functions/designRejectionNoteEdit";

type DesignSelection = {
  design: Design;
  intent: DesignModalIntent;
};

export function DesignListScreen(): React.ReactElement {
  const [query, queryChanger] = usePageQuery();

  // Reset fetched IDs when query changes (e.g., page change, filter change)
  // But ONLY if the query actually changes significantly (not just a re-render)
  // In fact, since we use react-query cache with staleTime, we should clear this set
  // only when we know we are getting fresh data without prices.
  // However, simple solution: If page changes, we likely need to fetch prices for that new page.
  // If we revisit a page, React Query might serve cached data which *already has prices*.
  // So we must check if the incoming data actually needs prices before deciding to fetch.
  // The logic inside useEffect does exactly that (filters for missing prices).
  // The only reason to reset this set is to allow retrying if for some reason we navigate away and back
  // and the cache was invalidated.

  useEffect(() => {
    // If the query changes (user navigates away), we should probably reset loadingPrices
    // to ensure the next page doesn't show "Loading..." if it has no data yet.
    // However, if we keepPreviousData=false, page will be undefined during loading.
  }, [
    query.page?.index,
    query.page?.size,
    query.searchTerm,
    query.sortBy?.field,
    query.sortBy?.sortDirection,
  ]);

  const { state, dataTransition } = usePagedComponentStateMachine();
  const {
    designRepository,
    organizationRepository,
    styleRepository,
    logoRepository,
    manufacturerRepository,
  } = useRepositoriesContext();
  const [lastPage, setLastPage] = useState<number>(1);
  const [designSelected, setDesignSelected] = useState<DesignSelection | null>(
    null
  );
  const [designRejection, setDesignRejection] = useState<Design | null>(null);
  const { role, b2CId } = useProfileContext();
  const rejectionNoteDialog = useDialog();
  const { result: openResult, clearOpen } = useOpenDesign();
  const location = useLocation();
  const [updatingDesignId] = useState(location.state?.updatingDesignId);
  // The popup's and the row's Edit handler (navigate only - no call).
  const { handleGoToUpdateDesign } = useDesignActionHandler();

  const {
    data: page,
    isSuccess: isDesignsSuccess,
    isLoading: isDesignsLoading,
    isError: isDesignsError,
    isFetching: isDesignsFetching,
    isPreviousData: isDesignsPreviousData,
  } = designRepository.useAllDesigns(query, { keepPreviousData: true });

  const {
    data: optionsPage,
    isSuccess: isOptionsSuccess,
    isLoading: isOptionsLoading,
    isError: isOptionsError,
  } = logoRepository.useOptions();

  const {
    data: stylesPage,
    isSuccess: isStylesSuccess,
    isLoading: isStylesLoading,
    isError: isStylesError,
  } = styleRepository.useAllStyles(getAllQuery);

  const {
    data: manufactuerersPage,
    isSuccess: isManufactererSuccess,
    isLoading: isManufactererLoading,
    isError: isManufactererError,
  } = manufacturerRepository.useManufacturers();

  const {
    customers,
    vendors,
    isError: isOrganizationsError,
    isSuccess: isOrganizationsSuccess,
    isLoading: isOrganizationsLoading,
  } = useCustomerAndVendors(organizationRepository);

  if (page && page.pageCount !== lastPage) {
    setLastPage(page.pageCount);
  }

  // keepPreviousData: the paginator can briefly show the previous result's
  // page range; if the user clicks past the new result's last page, clamp
  // back once fresh data lands (mirror of the logo list's guard).
  useEffect(() => {
    if (!page || isDesignsPreviousData) return;
    // An empty result (pageCount 0) would otherwise leave the index stranded
    // (e.g. "page 7 of 0") - park it on page 1.
    const lastValidPage = Math.max(page.pageCount, 1);
    if (query.page.index > lastValidPage) {
      queryChanger.handleNewPage({ value: String(lastValidPage) });
    }
  }, [page, isDesignsPreviousData, query.page.index, queryChanger]);

  // Warm next page's cache so sequential paging is instant (mirror of the
  // logo list's prefetch).
  useEffect(() => {
    // Skip on page 1 of a search (each keystroke would double the heavy
    // backend load with a hidden next-page call); once the user pages within
    // search results they have settled on the term. Also skip while previous
    // data is shown (its pageIndex/pageCount belong to the old query) - see
    // logo list.
    if (!page || page.pageIndex >= page.pageCount) return;
    if ((query.searchTerm && query.page.index === 1) || isDesignsPreviousData)
      return;
    // Flag designPrefetchLeanEnabled: no hidden next-page call for a result
    // over designPrefetchMaxTotal (large orgs - each page is a heavy
    // GetDesigns); an unknown total = today. Flag off = always prefetch.
    if (
      !shouldPrefetchDesignPage(
        page.totalCount,
        enviroment.designPrefetchMaxTotal,
        enviroment.designPrefetchLeanEnabled
      )
    )
      return;
    // Debounce (see logo list): skip prefetching pages the user rapidly jumps past.
    const timer = setTimeout(() => {
      designRepository.prefetchDesigns({
        ...query,
        page: { ...query.page, index: query.page.index + 1 },
      });
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page?.pageIndex,
    page?.pageCount,
    query.page.index,
    query.searchTerm,
    isDesignsPreviousData,
  ]);

  // Mirror of logo-list: warm the logo list's default first page so the first
  // hop Design -> Logo is instant (key matches a fresh visit by construction:
  // the user's remembered logo page size with the page-size memory flag on,
  // getInitialQuery(new URLSearchParams()) with it off).
  useEffect(() => {
    if (page) {
      logoRepository.prefetchLogos(prefetchListQuery("logo", b2CId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!page]);

  // Hover-prefetch for the paginator: warm the hovered page with exactly the
  // request a click would issue (prefetchDesigns shares the click's cache
  // key and staleTime, so this never changes what a click shows).
  function handlePrefetchPage(pageIndex: number) {
    // While previous data is shown (keepPreviousData) the paginator's page
    // range belongs to the OLD query - hovering it must not fire heavy calls
    // for potentially out-of-range pages of the new one.
    if (isDesignsPreviousData) return Promise.resolve();
    // Flag designPrefetchLeanEnabled: same limit as the next-page prefetch
    // above - the click itself still fetches as today.
    if (
      !shouldPrefetchDesignPage(
        page?.totalCount,
        enviroment.designPrefetchMaxTotal,
        enviroment.designPrefetchLeanEnabled
      )
    )
      return Promise.resolve();
    return designRepository.prefetchDesigns({
      ...query,
      page: { ...query.page, index: pageIndex },
    });
  }

  function showRejectionNote(design: Design) {
    setDesignRejection(design);
    rejectionNoteDialog.openDialog();
  }

  // Flag designRejectionNoteEditEnabled (Nichlas 18/10: the "See note"
  // dialog's Edit did nothing): Edit closes the dialog and opens the editor
  // exactly as the popup's Edit does - only when the popup would show Edit
  // for this design; otherwise the dialog hides the button. Flag off = no
  // extra props at all = today's dialog.
  function rejectionNoteEditProps(
    design: Design
  ): Pick<ComponentProps<typeof RejectionNoteDialog>, "showEdit" | "onEdit"> {
    if (!enviroment.designRejectionNoteEditEnabled) return {};
    const showEdit = canEditDesignFromRejectionNote(design, role);
    return {
      showEdit,
      onEdit: showEdit
        ? () => {
            rejectionNoteDialog.closeDialog();
            handleGoToUpdateDesign(design);
          }
        : undefined,
    };
  }

  // useEffect(() => {
  //   if (openResult) {
  //     setDesignSelected({ design: openResult, intent: 'view' })
  //   }
  // }, [openResult])

  // Admin perf FE-4 (flag adminPerfHelperCacheEnabled): soft gate - paint
  // when designs + orgs are ready; manufacturers/options/styles only feed
  // filter dropdowns and fill in when they land. Off = the all-queries gate.
  const { isLoading, isSuccess, isError } = designListGate({
    designs: {
      isLoading: isDesignsLoading,
      isSuccess: isDesignsSuccess,
      isError: isDesignsError,
    },
    organizations: {
      isLoading: isOrganizationsLoading,
      isSuccess: isOrganizationsSuccess,
      isError: isOrganizationsError,
    },
    manufacturers: {
      isLoading: isManufactererLoading,
      isSuccess: isManufactererSuccess,
      isError: isManufactererError,
    },
    options: {
      isLoading: isOptionsLoading,
      isSuccess: isOptionsSuccess,
      isError: isOptionsError,
    },
    styles: {
      isLoading: isStylesLoading,
      isSuccess: isStylesSuccess,
      isError: isStylesError,
    },
  });

  dataTransition(isLoading, isSuccess, isError);

  return state === PagedComponentState.Succeed ||
    state === PagedComponentState.PageLoading ? (
    <>
      {designRejection && (
        <RejectionNoteDialog
          title={<DesignTitleHeader design={designRejection!} />}
          dialogOptions={rejectionNoteDialog}
          approval={designRejection.approval!}
          exclude={role!}
          {...rejectionNoteEditProps(designRejection)}
        />
      )}
      <DesignListWrapper
        query={query}
        queryChanger={queryChanger}
        isPageLoading={state === PagedComponentState.PageLoading}
        isFetching={isDesignsFetching}
        isPreviousData={isDesignsPreviousData}
        totalCount={page?.totalCount}
        lastPage={lastPage}
        onPrefetchPage={handlePrefetchPage}
        onSelectDesign={(design) => {
          setDesignSelected({ design, intent: "view" });
        }}
        designs={page?.results}
        customers={customers!}
        vendors={vendors}
        styles={stylesPage?.results ?? []}
        methods={optionsPage?.transferMethods ?? []}
        manufacturers={manufactuerersPage ?? []}
        onShowRejectionNote={showRejectionNote}
        onApprove={(design) => setDesignSelected({ design, intent: "approve" })}
        onReject={(design) => setDesignSelected({ design, intent: "reject" })}
        updatingDesignId={updatingDesignId}
      />
      <ContentModal
        open={designSelected !== null}
        onClose={() => {
          setDesignSelected(null);
          clearOpen();
        }}
      >
        {designSelected !== null ? (
          <DesignDetailsWrapper
            design={designSelected!.design}
            onClose={() => setDesignSelected(null)}
            onShowRejectionNote={showRejectionNote}
            closeDialog={() => setDesignSelected(null)}
            intent={designSelected!.intent}
          />
        ) : (
          <></>
        )}
      </ContentModal>
    </>
  ) : state === PagedComponentState.Failed ? (
    <ErrorView />
  ) : (
    <LoadingView />
  );
}
